import {buildHomeOrganization, type HouseHome} from '@core/wall/home/doorstep'
import {FRONT_LINE, FRONT_TITLE} from '@product/ovid/proposition'
import {ovidTheme} from '@product/ovid/wall/theme'

import {homeFaq} from './faq'
import styles from './endpaper.module.scss'

/**
 * Ovid's home doorstep (plan `2026_09_17_ovid_veiled_wall.md` step 6b; the doorstep shape from
 * `2026_09_24_wall_home_doorstep.md`). No catalog and no pack index — the new host has no pack
 * pages (plan § 8: `/packs` and `/:packSlug` answer 404).
 *
 * The Organization is `ovid.gallery`'s own entity, not the shared `mirroraiOrganization` — that
 * constant's `sameAs` lists ovidmuse.com, which the host succession's "no link, no redirect" ruling
 * (step 9) forbids. No `sameAs` at all: the new host has no sibling to declare yet.
 *
 * COPY_STATUS marks every string below as placeholder, structure-final prose: plain, short, and
 * clean of the 2026-08-05 wording pivot's banned lexicon and of any explicit or anatomical
 * vocabulary (ruling 5 — the endpaper is written to be classified correctly first, to rank
 * second). The lexical gate (`__tests__/homeLexicalGate.spec.tsx`) checks the rendered doorstep
 * and `faq.ts` on every test run. The real copy is authored later, in the House voice.
 */
export const COPY_STATUS = 'placeholder' as const

const ORIGIN = 'https://ovid.gallery'
const ORGANIZATION = {name: 'Ovid'}

export const ovidHome: HouseHome = {
    origin: ORIGIN,
    meta: {title: FRONT_TITLE, description: FRONT_LINE},
    organization: ORGANIZATION,
    webApplication: {
        name: 'Ovid',
        description: FRONT_LINE,
        url: ORIGIN,
        applicationCategory: 'MultimediaApplication',
        creator: buildHomeOrganization(ORIGIN, ORGANIZATION),
    },
    tileImageUrl: ovidTheme.tileImageUrl,
    styles,
    endpaper: {
        intro: {
            h1: 'Ovid — a house of AI muses',
            lede:
                'Ovid designs muses — synthetic identities, never real people — and renders them into pictures, ' +
                'pack by pack, register by register. Nothing here is a photograph; every frame is built to order.',
        },
        sections: [
            {
                h2: 'How a picture is made',
                body: (
                    <p>
                        Start with a muse — an invented identity, cast once and reused. Add a line: a place, a mood, a
                        detail to carry through the set. The render follows the line, once, in one take.
                    </p>
                ),
            },
            {
                h2: 'The registers',
                body: (
                    <p>
                        Every pack lives in one register, named for where it happens: a coastal town, an alpine lodge,
                        an old atelier, a market at night. The place sets the light and the mood; the muse carries it
                        through the set.
                    </p>
                ),
            },
        ],
        faq: {h2: 'Questions', entries: homeFaq},
    },
}
